import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import creditCardTypes from '../../util/enum/api/creditCardTypes';
import { formatDate } from '../../util/helpers/dateHelpers';
import Paragraph from '../Paragraph/Paragraph';
import TypeList from '../TypeList/TypeList';
import Button from '../Button/Button';
import { ADD_CREDIT_CARD_WIZARD } from '../../constants/sessionStorage';
import { removeFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { uFlexCenter } from '../../assets/styles/utility';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { PAYMENT_METHOD_TYPES_ICONS } from '../../util/enum/api/paymentTypes';
import SelectItem from '../Buy/components/SelectItem';
import AddCreditDebitCardWizard from '../Modals/CreditCard/AddCreditDebitCardWizard';

export const AddCreditCardSection = styled.div`
  border: 1px solid ${themeColors.colorBorderPrimary};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: ${variables.shadow.boxShadow};
  ${({ disabled }) =>
    disabled &&
    `opacity: 0.5;
      pointer-events: none;
      `}
`;

export const AddCreditCardIconAndTextWrapper = styled.div`
  display: flex;
  flex: 1;
`;

export const AddCreditCardElement = styled(Button)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0;
  box-shadow: none;
  padding: ${pxToRem(12.5)} ${pxToRem(16.5)};
  text-transform: none;
  justify-content: flex-start;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  font-size: ${pxToRem(16)};
  width: 100%;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(16)};
  }
`;

export const AddCreditCardIcon = styled.div`
  ${uFlexCenter};
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  border-radius: 4px;
  background-color: ${themeColors.colorPrimary};
  box-shadow: ${variables.shadow.boxShadow};
  margin-right: ${pxToRem(10)};
  flex-shrink: 0;
  padding: ${pxToRem(3)};
  color: ${themeColors.colorWhite};

  svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
    color: ${themeColors.colorWhite};
  }
`;

export const HorizontalLine = styled.hr`
  width: 100%;
  height: 1px;
  background-color: ${themeColors.colorInputBorder};
  border-width: 0;
`;

export const AddCreditCardNote = styled.p`
  padding: 0px ${pxToRem(12.5)} ${pxToRem(12.5)} ${pxToRem(16.5)};
`;

const MaskedContainer = styled.div`
  font-size: 12px;
  color: ${themeColors.colorTextSecondary};
  display: flex;
  align-self: flex-start;

  span {
    font-size: 12px;
  }
`;

const AddPaymentMethodButton = styled.button`
  width: 100%;
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px dashed ${themeColors.colorGrey3};
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  background-color: transparent;
  padding: ${pxToRem(10)};
  cursor: pointer;
  margin-bottom: ${pxToRem(10)};
`;

export const AddCreditCardButtonText = styled.span``;

export const AddOrChooseCreditCard = ({
  creditCards,
  values,
  isReachedTransactionLimit,
  setSelectedCreditCard,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const [isAddCreditCardModalOpen, setIsAddCreditCardModalOpen] = useState(
    false,
  );
  const settings = useSelector(selectSettings);
  return (
    <>
      <Paragraph marginBottom={8} fontSize={16} bold>
        {t('storageFee.payStorageFeeByCardModal.selectACardForPayment')}
      </Paragraph>
      <AddPaymentMethodButton
        onClick={(e) => {
          e.preventDefault();
          setIsAddCreditCardModalOpen(true);
        }}
      >
        {t('creditCards.addNewCreditCard')}
      </AddPaymentMethodButton>
      {!isEmpty(creditCards) && (
        <>
          <TypeList gridRowGap={9}>
            {creditCards.map((card) => (
              <SelectItem
                key={card.CreditCardUid}
                selectItemTitle={t(
                  `creditCards.creditCardTypes.${
                    creditCardTypes[card.CreditCardType]
                  }`,
                )}
                selectItemSubtitle={
                  <MaskedContainer>
                    <span>***</span>
                    <span>{card?.Last4Digits}</span>
                  </MaskedContainer>
                }
                icon={PAYMENT_METHOD_TYPES_ICONS[3]}
                isSelected={values.creditCard === card.CreditCardUid}
                onClick={() => {
                  setSelectedCreditCard(card);
                  setFieldValue('creditCard', card.CreditCardUid);
                }}
                isDisabled={card.IsExpired}
                selectItemDescription={
                  <>
                    {!card.IsExpired && isReachedTransactionLimit && (
                      <Paragraph
                        color={themeColors.colorTextSecondary}
                        fontSize={13}
                        bold
                      >
                        {t(
                          'storageFee.payStorageFeeByCardModal.creditCardTransactionLimit',
                          {
                            transactionLimit: formatMoneyNumeral(
                              settings.CreditCardTransactionLimit,
                            ),
                          },
                        )}
                      </Paragraph>
                    )}
                    {card.IsExpired && (
                      <Paragraph
                        color={themeColors.colorError}
                        fontSize={13}
                        bold
                      >
                        {t('creditCards.expiredWithDate', {
                          expirationMonth: formatDate(
                            card.ExpirationDate,
                            'MM',
                          ),
                          expirationYear: formatDate(card.ExpirationDate, 'yy'),
                        })}
                      </Paragraph>
                    )}
                  </>
                }
              />
            ))}
          </TypeList>
          <HorizontalLine />
        </>
      )}
      <AddCreditDebitCardWizard
        isOpen={isAddCreditCardModalOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_CREDIT_CARD_WIZARD);
          setIsAddCreditCardModalOpen(false);
        }}
        // postPaidFlow
        // setIsNewCardAdded={setIsNewCardAdded}
        // setIsNewACHAdded={setIsNewACHAdded}
        // goToInitialModal={() => {}}
        // avoidClearingStorage
        // setIsAddingNewCard={setIsAddingNewCard}
      />
      {/* <AddCreditCardSection onClick={() => setIsAddCreditCardModalOpen(true)}>
        <AddCreditCardElement>
          <AddCreditCardIconAndTextWrapper>
            <AddCreditCardIcon>
              <Plus />
            </AddCreditCardIcon>
            <AddCreditCardButtonText>
              {t('creditCards.addNewCreditCard')}
            </AddCreditCardButtonText>
          </AddCreditCardIconAndTextWrapper>
          <BalanceCardIcon>
            <Caret />
          </BalanceCardIcon>
        </AddCreditCardElement>
        <AddCreditCardNote>
          {t('storageFee.payStorageFeeByCardModal.allMajorCardTypesAccepted')}
        </AddCreditCardNote>
      </AddCreditCardSection>
      <AddCreditCardWizard
        isOpen={isAddCreditCardModalOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_CREDIT_CARD_WIZARD);
          setIsAddCreditCardModalOpen(false);
        }}
      /> */}
    </>
  );
};

AddOrChooseCreditCard.propTypes = {
  creditCards: PropTypes.oneOfType([PropTypes.string, PropTypes.shape([])]),
  values: PropTypes.shape({
    creditCard: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  }),
  isReachedTransactionLimit: PropTypes.bool,
  setSelectedCreditCard: PropTypes.func,
  setFieldValue: PropTypes.func,
};
